import { AUDIO_EXTENSIONS, IMAGE_EXTENSIONS } from "./messages-constants";

export const parseDateValue = (value?: string | number | Date | null) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.valueOf()) ? null : numericDate;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      const numericDate = new Date(numeric);
      if (!Number.isNaN(numericDate.valueOf())) return numericDate;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.valueOf()) ? null : parsed;
  }
  return null;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const formatTimestamp = (value?: string | number | Date | null) => {
  const date = parseDateValue(value);
  if (!date) return "";

  const now = new Date();
  const sameDay = isSameDay(date, now);

  if (sameDay) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const getDateKey = (value?: string | number | Date | null) => {
  const date = parseDateValue(value);
  if (!date) return "";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

export const formatTime = (value?: string | number | Date | null) => {
  const date = parseDateValue(value);
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const formatDateLabel = (value?: string | number | Date | null) => {
  const date = parseDateValue(value);
  if (!date) return "";
  const now = new Date();
  if (isSameDay(date, now)) return "Today";
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return "Yesterday";
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };
  if (date.getFullYear() !== now.getFullYear()) {
    options.year = "numeric";
  }
  return date.toLocaleDateString([], options);
};

export const formatDateTimeLabel = (value?: string | number | Date | null) => {
  const dateLabel = formatDateLabel(value);
  const timeLabel = formatTime(value);
  if (dateLabel && timeLabel) return `${dateLabel}, ${timeLabel}`;
  return dateLabel || timeLabel;
};

export const getMessageTimestampValue = (message: any) =>
  message?.createdAt || message?.updatedAt || null;

export const getOtherParticipant = (
  thread: any,
  userId: string | undefined,
  role: string | undefined
) => {
  if (!thread?.participants?.length) return null;
  return thread.participants.find(
    (participant: any) =>
      participant?.userId?.toString() !== userId || participant?.role !== role
  );
};

export const getAttachmentUrl = (file: any) => {
  const candidate = file?.url || file?.path || "";
  return typeof candidate === "string" ? candidate : "";
};

export const getAttachmentExtension = (file: any) => {
  const candidates = [file?.title, file?.name, getAttachmentUrl(file), file?.path];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const cleaned = candidate.split("?")[0].split("#")[0];
    const match = cleaned.match(/\.([a-zA-Z0-9]+)$/);
    if (match?.[1]) return match[1].toLowerCase();
  }
  return "";
};

export const getAttachmentType = (file: any) => {
  const ext = getAttachmentExtension(file);
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (AUDIO_EXTENSIONS.has(ext)) return "audio";
  return "file";
};

export const truncateText = (value: string, maxLength = 80) => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}...`;
};

export const getMessagePreview = (message: any) => {
  if (!message) return "";
  const body = message?.body?.trim() || "";
  if (body) return truncateText(body);
  const attachments = Array.isArray(message?.attachments)
    ? message.attachments
    : [];
  if (!attachments.length) return "Message";
  const first = attachments[0];
  return truncateText(first?.title || first?.name || "Attachment");
};

export const getMessageThreadId = (message: any) =>
  message?.threadId?.toString?.() || message?.threadId || "";

export const buildReactionSummary = (
  reactions: any[],
  userId?: string,
  role?: string
) => {
  const map = new Map<
    string,
    { emoji: string; count: number; reacted: boolean }
  >();
  reactions.forEach((reaction: any) => {
    const emoji = reaction?.emoji;
    if (!emoji) return;
    const entry = map.get(emoji) || {
      emoji,
      count: 0,
      reacted: false,
    };
    entry.count += 1;
    if (reaction?.userId?.toString?.() === userId && reaction?.role === role) {
      entry.reacted = true;
    }
    map.set(emoji, entry);
  });
  return Array.from(map.values());
};
